import { useEffect } from "react";
import { db, doc, onSnapshot, updateDoc, deleteField } from "../lib/firebase";
import { usePartyStore } from "../stores/partyStore";
import { useAuthStore } from "../stores/authStore";
import type { Party, PartyWrappedStats } from "../types";

export function usePartySync(partyId: string | null) {
  useEffect(() => {
    if (!partyId) {
      if (usePartyStore.getState().currentParty !== null) {
        usePartyStore.getState().resetParty();
      }
      return;
    }

    const partyRef = doc(db, "parties", partyId);
    const unsubscribe = onSnapshot(
      partyRef,
      (docSnap) => {
        const { queue, setPartyEndedSummary, resetParty, setParty, setUserRole } = usePartyStore.getState();
        const { user, setUser } = useAuthStore.getState();

        if (!docSnap.exists()) {
          // Party was deleted / ended
          const singerMap: Record<string, { count: number; photoURL: string | null }> = {};
          queue.forEach((q) => {
            if (!singerMap[q.addedByUserName]) {
              singerMap[q.addedByUserName] = { count: 0, photoURL: q.addedByUserPhoto || null };
            }
            singerMap[q.addedByUserName].count += 1;
          });

          const topSingers = Object.entries(singerMap)
            .map(([name, data]) => ({ name, count: data.count, photoURL: data.photoURL }))
            .sort((a, b) => b.count - a.count);

          const summary: PartyWrappedStats = {
            totalSongsSung: queue.length,
            partyDurationMinutes: 0,
            topSingers,
            allSongs: queue,
          };

          setPartyEndedSummary(summary);
          resetParty();

          if (user) {
            setUser({ ...user, currentPartyId: null });
            updateDoc(doc(db, "users", user.uid), {
              currentPartyId: deleteField(),
            }).catch(console.error);
          }
          return;
        }

        const data = docSnap.data();
        const partyData: Party = {
          id: docSnap.id,
          partyName: data.partyName || "Karaoke Party",
          partyCode: data.partyCode || "",
          hostId: data.hostId || "",
          hostName: data.hostName || "Host",
          status: data.status || "active",
          createdAt: data.createdAt,
          currentSongId: data.currentSongId || null,
          isPlaying: data.isPlaying ?? true,
        };

        setParty(partyData);

        if (user) {
          const role = partyData.hostId === user.uid ? "HOST" : "GUEST";
          setUserRole(role);
        }
      },
      (error) => {
        if (error.code !== "permission-denied") {
          console.error("Party sync error:", error);
        }
      }
    );

    return () => unsubscribe();
  }, [partyId]);
}
